import creditEngineConfig from "../../config/creditEngineConfig.json";

export interface OperatorOption {
  valor: string;
  label: string;
  descricao: string;
  tipo_input: "text" | "number" | "date" | "email" | "tel";
}

export interface FieldOption {
  valor: string;
  label: string;
  tipo: string;
  tipo_input:
    | "text"
    | "number"
    | "date"
    | "email"
    | "tel"
    | "select"
    | "checkbox"
    | "color"
    | "boolean";
  descricao: string;
  placeholder?: string;
  obrigatorio: boolean;
  opcoes?: Array<{ valor: string; label: string }>;
}

export interface DataSource {
  valor: string;
  label: string;
  descricao: string;
  campos: FieldOption[];
}

export interface CreditEngineConfig {
  operadores: OperatorOption[];
  fontes_dados: DataSource[];
}

const config: CreditEngineConfig = creditEngineConfig as CreditEngineConfig;

export const getOperators = (): OperatorOption[] => config.operadores;

export const getOperatorByValue = (valor: string): OperatorOption | undefined =>
  config.operadores.find((op) => op.valor === valor);

export const getDataSources = (): DataSource[] => config.fontes_dados;

export const getDataSourceByValue = (valor: string): DataSource | undefined =>
  config.fontes_dados.find((fonte) => fonte.valor === valor);

export const getFieldsByDataSource = (
  dataSourceValue: string
): FieldOption[] => {
  const dataSource = getDataSourceByValue(dataSourceValue);
  return dataSource?.campos || [];
};

export const getFieldByValue = (
  dataSourceValue: string,
  fieldValue: string
): FieldOption | undefined => {
  const fields = getFieldsByDataSource(dataSourceValue);
  return fields.find((field) => field.valor === fieldValue);
};

export const getCompatibleOperators = (
  field: FieldOption
): OperatorOption[] => {
  if (field.tipo === "boolean" || field.tipo_input === "boolean") {
    return config.operadores.filter((op) => ["==", "!="].includes(op.valor));
  }

  if (field.tipo_input === "number") {
    return config.operadores.filter((op) =>
      ["number", "text"].includes(op.tipo_input)
    );
  }
  if (
    field.tipo_input === "text" ||
    field.tipo_input === "email" ||
    field.tipo_input === "tel"
  ) {
    return config.operadores.filter((op) => op.tipo_input === "text");
  }

  if (field.tipo_input === "date") {
    return config.operadores.filter((op) =>
      [">", ">=", "<", "<=", "==", "!="].includes(op.valor)
    );
  }

  if (field.tipo_input === "select" && field.tipo === "enum") {
    return config.operadores.filter((op) =>
      ["==", "!=", "IN"].includes(op.valor)
    );
  }

  return config.operadores;
};

export const getOperatorsForSelect = (): Array<{
  value: string;
  label: string;
}> => config.operadores.map((op) => ({ value: op.valor, label: op.label }));

export const getDataSourcesForSelect = (): Array<{
  value: string;
  label: string;
}> =>
  config.fontes_dados.map((fonte) => ({
    value: fonte.valor,
    label: fonte.label,
  }));

export const getFieldsForSelect = (
  dataSourceValue: string
): Array<{ value: string; label: string }> => {
  const fields = getFieldsByDataSource(dataSourceValue);
  return fields.map((field) => ({ value: field.valor, label: field.label }));
};
